import { useEffect, useMemo, useRef, useState } from 'react';
import { logger } from '../../../shared/utils/logger.js';
import { employeesApi, type EmployeeDocumentRow, type EmployeeDocumentType, type EmployeeRow } from '../api/employees.api';
import {
  useDeleteEmployeeDocument,
  useEmployeeDocuments,
  useRequestEmployeeDocumentAccessOtp,
  useUploadEmployeeDocument,
  useVerifyEmployeeDocumentAccessOtp,
} from '../queries';

type EmployeeDocumentsModalProps = {
  open: boolean;
  employee: EmployeeRow | null;
  onClose: () => void;
  onToast?: (toast: { type: 'success' | 'error'; message: string }) => void;
};

const MODULE = 'employees-roles-employee-documents-modal';
const MAX_DOCUMENTS = 3;
const MAX_FILE_SIZE_LABEL = '10 MB';
const DOCUMENT_TYPE_OPTIONS: Array<{ value: EmployeeDocumentType; label: string }> = [
  { value: 'AADHAAR', label: 'Aadhaar card' },
  { value: 'DRIVING_LICENSE', label: 'Driving license' },
  { value: 'OFFICIAL_ID_OTHER', label: 'Other official ID' },
];

const logEvent = (event: string, fields: Record<string, unknown> = {}): void => {
  logger.info(MODULE, event, { event, ...fields });
};

const DOCUMENT_TYPE_LABELS: Record<string, string> = {
  AADHAAR: 'Aadhaar card',
  DRIVING_LICENSE: 'Driving license',
  OFFICIAL_ID_OTHER: 'Other official ID',
};

const formatDocumentTypeLabel = (documentType: string): string =>
  DOCUMENT_TYPE_LABELS[documentType] || 'Official document';

const formatFileSize = (fileSizeBytes: number): string => {
  if (!Number.isFinite(fileSizeBytes) || fileSizeBytes <= 0) {
    return 'Size unavailable';
  }
  if (fileSizeBytes >= 1024 * 1024) {
    return `${(fileSizeBytes / (1024 * 1024)).toFixed(1)} MB`;
  }
  return `${Math.max(1, Math.round(fileSizeBytes / 1024))} KB`;
};

const isPreviewableContentType = (contentType: string): boolean =>
  contentType === 'application/pdf'
  || contentType.startsWith('image/');

const EmployeeDocumentsModal = ({
  open,
  employee,
  onClose,
  onToast,
}: EmployeeDocumentsModalProps) => {
  const [documentType, setDocumentType] = useState<EmployeeDocumentType>('AADHAAR');
  const [documentDisplayName, setDocumentDisplayName] = useState('');
  const [documentFile, setDocumentFile] = useState<File | null>(null);
  const [documentLocalError, setDocumentLocalError] = useState('');
  const [accessDocument, setAccessDocument] = useState<EmployeeDocumentRow | null>(null);
  const [maskedEmail, setMaskedEmail] = useState('');
  const [accessOtp, setAccessOtp] = useState('');
  const [accessLocalError, setAccessLocalError] = useState('');
  const [previewDocument, setPreviewDocument] = useState<{ name: string; contentType: string; url: string; fileName: string; documentUuid: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const previewUrlRef = useRef<string | null>(null);

  const employeeUuid = employee?.employeeUuid || null;
  const documentsQuery = useEmployeeDocuments(employeeUuid);
  const uploadDocumentMutation = useUploadEmployeeDocument(employeeUuid || '');
  const deleteDocumentMutation = useDeleteEmployeeDocument(employeeUuid || '');
  const requestAccessOtpMutation = useRequestEmployeeDocumentAccessOtp(employeeUuid || '');
  const verifyAccessOtpMutation = useVerifyEmployeeDocumentAccessOtp(employeeUuid || '');
  const uploadedDocuments = documentsQuery.data || [];
  const documentSlotsRemaining = Math.max(0, MAX_DOCUMENTS - uploadedDocuments.length);

  const selectedFileLabel = useMemo(() => {
    if (!documentFile) return 'No file selected';
    return `${documentFile.name} (${Math.round(documentFile.size / 1024)} KB)`;
  }, [documentFile]);

  useEffect(() => {
    if (!open) return;
    setDocumentType('AADHAAR');
    setDocumentDisplayName('');
    setDocumentFile(null);
    setDocumentLocalError('');
    setAccessDocument(null);
    setMaskedEmail('');
    setAccessOtp('');
    setAccessLocalError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    logEvent('employee_documents_modal_open', {
      employeeUuid,
    });
  }, [open, employeeUuid]);

  const clearPreview = () => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    setPreviewDocument(null);
  };

  const triggerBlobDownload = (url: string, fileName: string) => {
    const downloadLink = document.createElement('a');
    downloadLink.href = url;
    downloadLink.download = fileName;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  };

  // Re-download the already-fetched file from the in-memory blob — no new OTP needed,
  // since the document is still held client-side while the preview is open.
  const handleDownloadAgain = () => {
    if (!previewDocument) return;
    triggerBlobDownload(previewDocument.url, previewDocument.fileName);
    logEvent('employee_document_download_again', {
      employeeUuid,
      documentUuid: previewDocument.documentUuid,
    });
    onToast?.({ type: 'success', message: 'Document download started.' });
  };

  useEffect(() => () => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
    }
  }, []);

  if (!open || !employee) return null;

  const resetComposer = () => {
    setDocumentType('AADHAAR');
    setDocumentDisplayName('');
    setDocumentFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUploadDocument = () => {
    if (!employeeUuid) return;
    if (uploadedDocuments.length >= MAX_DOCUMENTS) {
      setDocumentLocalError('This employee already has the maximum of 3 documents.');
      return;
    }
    if (!documentFile) {
      setDocumentLocalError('Choose a file before uploading.');
      return;
    }

    setDocumentLocalError('');
    uploadDocumentMutation.mutate(
      {
        documentType,
        displayName: documentDisplayName.trim(),
        file: documentFile,
      },
      {
        onSuccess: () => {
          logEvent('employee_document_upload_modal_success', {
            employeeUuid,
            documentType,
          });
          onToast?.({ type: 'success', message: 'Employee document uploaded.' });
          resetComposer();
        },
        onError: (error) => {
          const message =
            error instanceof Error && error.message
              ? error.message
              : 'Could not upload employee document.';
          setDocumentLocalError(message);
          onToast?.({ type: 'error', message });
        },
      },
    );
  };

  const handleRemoveDocument = (documentUuid: string) => {
    setDocumentLocalError('');
    deleteDocumentMutation.mutate(documentUuid, {
      onSuccess: () => {
        onToast?.({ type: 'success', message: 'Employee document removed.' });
      },
      onError: (error) => {
        const message =
          error instanceof Error && error.message
            ? error.message
            : 'Could not remove employee document.';
        setDocumentLocalError(message);
        onToast?.({ type: 'error', message });
      },
    });
  };

  const handleRequestAccessOtp = async (document: EmployeeDocumentRow) => {
    if (!employeeUuid) return;
    if (
      accessDocument?.documentUuid === document.documentUuid
      && maskedEmail
      && !requestAccessOtpMutation.isPending
    ) {
      setAccessLocalError('');
      onToast?.({ type: 'success', message: `Use the OTP sent to ${maskedEmail}, or tap Resend OTP.` });
      return;
    }
    setAccessDocument(document);
    setAccessOtp('');
    setAccessLocalError('');
    clearPreview();
    try {
      const response = await requestAccessOtpMutation.mutateAsync(document.documentUuid);
      setMaskedEmail(response.maskedEmail);
      onToast?.({ type: 'success', message: `OTP sent to ${response.maskedEmail}.` });
    } catch (error) {
      const message = error instanceof Error && error.message
        ? error.message
        : 'Could not send document access OTP.';
      setAccessLocalError(message);
      onToast?.({ type: 'error', message });
    }
  };

  const handleVerifyAccessOtp = async () => {
    if (!employeeUuid || !accessDocument) return;
    const normalizedOtp = accessOtp.trim();
    if (!/^\d{6}$/.test(normalizedOtp)) {
      setAccessLocalError('OTP must be a 6-digit number.');
      return;
    }

    setAccessLocalError('');
    try {
      const grant = await verifyAccessOtpMutation.mutateAsync({
        documentUuid: accessDocument.documentUuid,
        otp: normalizedOtp,
      });
      const blob = await employeesApi.downloadEmployeeDocument(
        employeeUuid,
        accessDocument.documentUuid,
        grant.accessToken,
      );
      const objectUrl = URL.createObjectURL(blob);
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
      }
      previewUrlRef.current = objectUrl;

      const downloadFileName = accessDocument.fileName || `${accessDocument.displayName || 'employee-document'}`;
      triggerBlobDownload(objectUrl, downloadFileName);

      setPreviewDocument({
        name: accessDocument.displayName || accessDocument.fileName,
        contentType: blob.type || accessDocument.contentType,
        url: objectUrl,
        fileName: downloadFileName,
        documentUuid: accessDocument.documentUuid,
      });
      onToast?.({ type: 'success', message: 'Document download started. Preview ready below.' });
      // The OTP is single-use and now consumed server-side. Reset the access flow so the next
      // "Download & preview" requests a fresh OTP instead of prompting to reuse the spent one
      // (which would 400). In-session re-downloads use the "Download again" button instead.
      setAccessOtp('');
      setAccessDocument(null);
      setMaskedEmail('');
    } catch (error) {
      const message = error instanceof Error && error.message
        ? error.message
        : 'Could not verify OTP for document access.';
      setAccessLocalError(message);
      onToast?.({ type: 'error', message });
    }
  };

  return (
    <div className="role-editor-overlay" role="dialog" aria-modal="true" aria-label="Employee documents">
      <div className="role-editor role-editor--documents">
        <div className="role-editor__confirm-header">
          <h3 className="role-editor__title">Employee documents</h3>
          <p className="role-editor__confirm-copy">
            Manage official documents for <strong>{employee.name}</strong>. Maximum {MAX_DOCUMENTS} files, up to {MAX_FILE_SIZE_LABEL} each.
          </p>
        </div>

        <section className="role-editor__documents" aria-label="Official documents">
          <div className="role-editor__documents-header">
            <div>
              <h4 className="role-editor__documents-title">Official documents</h4>
              <p className="role-editor__documents-copy">
                Accepted files: PDF, JPEG, PNG, and WEBP. Max size: {MAX_FILE_SIZE_LABEL}.
              </p>
            </div>
            <span className="role-editor__documents-count">
              {uploadedDocuments.length}/{MAX_DOCUMENTS}
            </span>
          </div>

          {documentsQuery.isLoading ? (
            <div className="role-editor__documents-empty">Loading documents…</div>
          ) : documentsQuery.isError ? (
            <div className="role-editor__documents-error" role="alert">
              Could not load employee documents.
            </div>
          ) : uploadedDocuments.length === 0 ? (
            <div className="role-editor__documents-empty">
              No documents uploaded yet.
            </div>
          ) : (
            <div className="role-editor__document-list">
              {uploadedDocuments.map((document) => (
                <div key={document.documentUuid} className="role-editor__document-row">
                  <div className="role-editor__document-main">
                    <div className="role-editor__document-name">{document.displayName || document.fileName}</div>
                    <div className="role-editor__document-meta">
                      {formatDocumentTypeLabel(document.documentType)} • {formatFileSize(document.fileSizeBytes)}
                    </div>
                  </div>
                  <div className="role-editor__document-actions">
                    <button
                      type="button"
                      className="role-editor__document-action role-editor__document-action--preview"
                      onClick={() => void handleRequestAccessOtp(document)}
                      disabled={
                        requestAccessOtpMutation.isPending
                        || verifyAccessOtpMutation.isPending
                        || deleteDocumentMutation.isPending
                        || uploadDocumentMutation.isPending
                      }
                    >
                      {requestAccessOtpMutation.isPending && accessDocument?.documentUuid === document.documentUuid
                        ? 'Sending OTP…'
                        : 'Download & preview'}
                    </button>
                    <button
                      type="button"
                      className="role-editor__document-action"
                      onClick={() => handleRemoveDocument(document.documentUuid)}
                      disabled={deleteDocumentMutation.isPending || uploadDocumentMutation.isPending}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {accessDocument ? (
            <div className="role-editor__security-panel">
              <div className="role-editor__security-header">
                <div>
                  <h4 className="role-editor__documents-title">Verify document access</h4>
                  <p className="role-editor__documents-copy">
                    Enter the 6-digit OTP sent to {maskedEmail || 'the registered store email'} to unlock {accessDocument.displayName || accessDocument.fileName}.
                  </p>
                </div>
                <button
                  type="button"
                  className="role-editor__cancel"
                  onClick={() => {
                    setAccessDocument(null);
                    setMaskedEmail('');
                    setAccessOtp('');
                    setAccessLocalError('');
                  }}
                >
                  Dismiss
                </button>
              </div>

              <label className="role-editor__field">
                <span className="role-editor__label">Email OTP</span>
                <input
                  className="role-editor__input"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  autoComplete="one-time-code"
                  value={accessOtp}
                  onChange={(e) => setAccessOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="Enter 6-digit OTP"
                  disabled={verifyAccessOtpMutation.isPending}
                />
              </label>

              <div className="role-editor__security-actions">
                <button
                  type="button"
                  className="role-editor__file-trigger"
                  onClick={() => void handleRequestAccessOtp(accessDocument)}
                  disabled={requestAccessOtpMutation.isPending || verifyAccessOtpMutation.isPending}
                >
                  {requestAccessOtpMutation.isPending ? 'Resending…' : 'Resend OTP'}
                </button>
                <button
                  type="button"
                  className="role-editor__submit"
                  onClick={() => void handleVerifyAccessOtp()}
                  disabled={requestAccessOtpMutation.isPending || verifyAccessOtpMutation.isPending}
                >
                  {verifyAccessOtpMutation.isPending ? 'Verifying…' : 'Verify OTP'}
                </button>
              </div>

              {accessLocalError ? (
                <p className="role-editor__documents-error" role="alert">
                  {accessLocalError}
                </p>
              ) : null}
            </div>
          ) : null}

          <div className="role-editor__documents-composer">
            <label className="role-editor__field">
              <span className="role-editor__label">Document type</span>
              <select
                className="role-editor__input"
                value={documentType}
                onChange={(e) => setDocumentType(e.target.value as EmployeeDocumentType)}
                disabled={uploadDocumentMutation.isPending || documentSlotsRemaining === 0}
              >
                {DOCUMENT_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="role-editor__field">
              <span className="role-editor__label">Display name (optional)</span>
              <input
                className="role-editor__input"
                type="text"
                maxLength={120}
                value={documentDisplayName}
                onChange={(e) => setDocumentDisplayName(e.target.value)}
                disabled={uploadDocumentMutation.isPending || documentSlotsRemaining === 0}
                placeholder="Front ID, Back ID, License copy"
              />
            </label>

            <div className="role-editor__field">
              <span className="role-editor__label">File</span>
              <div className="role-editor__file-picker">
                <input
                  ref={fileInputRef}
                  className="role-editor__file-input-native"
                  type="file"
                  aria-label="File"
                  accept=".pdf,image/jpeg,image/png,image/webp"
                  onChange={(e) => setDocumentFile(e.target.files?.[0] || null)}
                  disabled={uploadDocumentMutation.isPending || documentSlotsRemaining === 0}
                />
                <button
                  type="button"
                  className="role-editor__file-trigger"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadDocumentMutation.isPending || documentSlotsRemaining === 0}
                >
                  Choose file
                </button>
                <span className="role-editor__file-name">{selectedFileLabel}</span>
              </div>
            </div>

            <div className="role-editor__documents-footer">
              <p className="role-editor__documents-note">
                {documentSlotsRemaining === 0
                  ? 'Document limit reached. Remove one to upload another.'
                  : `${documentSlotsRemaining} upload slot${documentSlotsRemaining === 1 ? '' : 's'} remaining.`}
              </p>
              <button
                type="button"
                className="role-editor__submit"
                onClick={handleUploadDocument}
                disabled={
                  uploadDocumentMutation.isPending ||
                  deleteDocumentMutation.isPending ||
                  documentSlotsRemaining === 0
                }
              >
                {uploadDocumentMutation.isPending ? 'Uploading…' : 'Upload document'}
              </button>
            </div>
          </div>

          {(documentLocalError || uploadDocumentMutation.error || deleteDocumentMutation.error) && (
            <p className="role-editor__documents-error" role="alert">
              {documentLocalError
                || (uploadDocumentMutation.error instanceof Error
                  ? uploadDocumentMutation.error.message
                  : '')
                || (deleteDocumentMutation.error instanceof Error
                  ? deleteDocumentMutation.error.message
                  : '')}
            </p>
          )}
        </section>

        <div className="role-editor__actions">
          <button type="button" className="role-editor__cancel" onClick={onClose}>
            Close
          </button>
        </div>
      </div>

      {previewDocument ? (
        <div className="role-editor-overlay" role="dialog" aria-modal="true" aria-label="Document preview">
          <div className="role-editor role-editor--documents role-editor--preview">
            <div className="role-editor__confirm-header">
              <h3 className="role-editor__title">Document preview</h3>
              <p className="role-editor__confirm-copy">{previewDocument.name}</p>
            </div>

            <div className="role-editor__preview-body">
              {isPreviewableContentType(previewDocument.contentType) ? (
                previewDocument.contentType === 'application/pdf' ? (
                  <iframe
                    title="Employee document preview"
                    className="role-editor__preview-frame"
                    src={previewDocument.url}
                  />
                ) : (
                  <img
                    className="role-editor__preview-image"
                    src={previewDocument.url}
                    alt={previewDocument.name}
                  />
                )
              ) : (
                <div className="role-editor__documents-empty">
                  Preview is unavailable for this file type after download.
                </div>
              )}
            </div>

            <div className="role-editor__actions">
              <button
                type="button"
                className="role-editor__document-action role-editor__document-action--preview"
                onClick={handleDownloadAgain}
              >
                Download again
              </button>
              <button type="button" className="role-editor__cancel" onClick={clearPreview}>
                Close preview
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default EmployeeDocumentsModal;
